import { Fragment, useState, useMemo, MutableRefObject, useEffect } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import dayjs, { Dayjs } from "dayjs";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Dialog } from "primereact/dialog";
import { Grid } from "@mui/material";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { CardActions, CardOverflow } from "@mui/joy";
import useApiService from "../../../hooks/useApiService";
import { siteListUpdate } from "../../../service/siteService";
import {
  siteScheduleExceptionAdd,
  siteScheduleExceptionRemove,
  siteScheduleExceptionUpdate,
} from "../../../service/siteScheduleExceptionService";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useToast } from "../../../context/ToastContext";

interface Schedule {
  [key: string]: {
    active: boolean;
    start?: string;
    end?: string;
  };
}

interface CalendarEvent {
  id: string;
  active: boolean;
  start?: string;
  end?: string;
  date: string;
  color?: string;
  type?: "default" | "exception";
}

interface SiteCalenderProps {
  defaultSchedule: Schedule;
  site_id: string;
}

function SiteCalenderView({ defaultSchedule, site_id }: SiteCalenderProps) {
  const toast = useToast();
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedException, setSelectedException] = useState<CalendarEvent | null>(null);

  const [editSchedule, setEditSchedule] = useState<boolean>(false);
  const [reloadExceptions, setReloadExceptions] = useState<boolean>(false);

  const [startTime, setStartTime] = useState<Date | undefined>(undefined);
  const [endTime, setEndTime] = useState<Date | undefined>(undefined);
  const [checked, setChecked] = useState<boolean>(false);

  const { data, loading, error } = useApiService<CalendarEvent[]>({
    method: "GET",
    url: `site-schedule-exception`,
    apiName: "core",
    onSuccess(data) {
      setExceptions(
        data.map(event => ({
          ...event,
          color: event.color || "#f57c00",
          date: dayjs(event.date).format("YYYY-MM-DD"),
          start: dayjs(event.start).format("HH:mm") || undefined,
          end: dayjs(event.end).format("HH:mm") || undefined,
          type: "exception",
        }))
      );
    },
    dependencies: [reloadExceptions],
  });

  // Sample events - replace with actual data
  const [exceptions, setExceptions] = useState<CalendarEvent[]>([]);

  const [schedule, setSchedule] = useState<Schedule>(
    defaultSchedule || {
      monday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      tuesday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      wednesday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      thursday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      friday: {
        active: true,
        start: "8:00",
        end: "17:00",
      },
      saturday: {
        active: false,
      },
      sunday: {
        active: false,
      },
    }
  );

  const monthStart = currentDate.startOf("month");
  const monthEnd = currentDate.endOf("month");
  const calendarStart = monthStart.startOf("week");
  const calendarEnd = monthEnd.endOf("week");

  const calendarDays = useMemo(() => {
    const days = [];
    let day = calendarStart;

    while (day <= calendarEnd) {
      days.push(day);
      day = day.add(1, "day");
    }

    return days;
  }, [calendarStart, calendarEnd]);


  useEffect(() => {
    if (defaultSchedule) {
      setSchedule(defaultSchedule);
    }
  }, [defaultSchedule]);

  const getEventsForDate = (date: Dayjs) => {
    const exception = exceptions.find(event => dayjs(event.date).isSame(date, "day"));
    if (exception) return exception;
    const scheduledEvent = Object.entries(schedule).find(([day, { active }]) => {
      return active && dayjs(date).format("dddd").toLowerCase() === day;
    });
    if (scheduledEvent) {
      return {
        id: `schedule-${date.format("YYYY-MM-DD")}`,
        active: true,
        start: scheduledEvent[1].start,
        end: scheduledEvent[1].end,
        date: date.format("YYYY-MM-DD"),
        color: "#1976d2",
        type: "default",
      } as CalendarEvent;
    }
    return {
      id: `schedule-${date.format("YYYY-MM-DD")}`,
      active: false,
      date: date.format("YYYY-MM-DD"),
      color: "#f44336",
      type: "default",
    } as CalendarEvent;
  };

  const updateSiteSchedule = async () => {
    try {
      const update = await siteListUpdate({ default_schedule: schedule }, site_id);
      toast("success", "Schedule Updated Successfully");
    } catch (error) {
      toast("error", "Update Schedule Failed");
    }
  };

  const updateAddException = async () => {
    try {
      console.log("selectedException` == ", selectedException);
      if (selectedException?.type == "default") {
        await siteScheduleExceptionAdd({
          date: selectedException.date,
          active: checked,
          start: startTime,
          end: endTime,
        });

        toast("success", "Exception Created Successfully");
      } else {
        await siteScheduleExceptionUpdate(
          {
            date: selectedException?.date,
            active: checked,
            start: startTime,
            end: endTime,
          },
          selectedException?.id!
        );
        toast("success", "Exception Updated Successfully");
      }
    } catch (error) {
      toast("error", "Update Schedule Failed");
    }
    setReloadExceptions(prev => !prev);
    setSelectedException(null);
    return;
  };

  const removeException = async () => {
    try {
      console.log("selectedException` == ", selectedException);
      await siteScheduleExceptionRemove(selectedException?.id!);

      toast("success", "Exception Removed Successfully");
    } catch (error) {
      toast("error", "Failed to Remove Exception");
    }
    setReloadExceptions(prev => !prev);
    setSelectedException(null);
    return;
  };

  const removeExceptionConfirm = () => {
    confirmDialog({
      message: "Are you sure you want to delete this exception?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => removeException(),
    });
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate(prev =>
      direction === "prev" ? prev.subtract(1, "month") : prev.add(1, "month")
    );
  };

  const goToToday = () => {
    setCurrentDate(dayjs());
    setSelectedDate(dayjs());
  };

  const handleDateClick = (date: Dayjs, event: CalendarEvent) => {
    console.log("Date clicked:", date.format("YYYY-MM-DD"));
    console.log("Event:", event);
    setSelectedDate(date);
    setSelectedException(event);
    setStartTime(event.start ? new Date(`${event.date} ${event.start}`) : undefined);
    setEndTime(event.end ? new Date(`${event.date} ${event.end}`) : undefined);
    setChecked(event.active || false);
  };

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const scheduleOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  return (
    <Fragment>
      <div style={{ padding: "1.5rem", margin: "0 auto" }}>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          {/* Left Card - 3/12 columns (25% width) */}
          <Card style={{ flex: "0 0 25%", padding: "1.5rem", position: "relative" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                marginBottom: "5%",
                marginTop: "-16%",
              }}
            >
              <Button
                icon="pi pi-pen-to-square"
                outlined
                onClick={() => setEditSchedule(true)}
                className="calender-edit-schedule-button"
                severity="warning"
                size="small"
              />
            </div>
            <h2 style={{ textAlign: "center", margin: "0 0 1rem 0" }}>Default Schedule</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {scheduleOrder.map(day => {
                const { active, start, end } = schedule[day];
                return (
                  <div
                    key={day}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </span>
                    <span style={{ color: active ? "#4caf50" : "#f44336" }}>
                      {active ? `${start} - ${end}` : "Closed"}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Right Card - 9/12 columns (75% width) */}
          <Card style={{ flex: "1", padding: "1.5rem", borderRadius: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h1 style={{ fontSize: "2rem", fontWeight: 600, color: "#1976d2", margin: 0 }}>
                {currentDate.format("MMMM YYYY")}
              </h1>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <Button
                  icon="pi pi-chevron-left"
                  onClick={() => navigateMonth("prev")}
                  className="p-button-outlined"
                  style={{ backgroundColor: "#f5f5f5" }}
                />

                <Button
                  icon="pi pi-calendar"
                  onClick={goToToday}
                  className="p-button-primary"
                  style={{ backgroundColor: "#1976d2" }}
                />

                <Button
                  icon="pi pi-chevron-right"
                  onClick={() => navigateMonth("next")}
                  className="p-button-outlined"
                  style={{ backgroundColor: "#f5f5f5" }}
                />
              </div>
            </div>

            {/* Week Days Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                marginBottom: "0.5rem",
              }}
            >
              {weekDays.map(day => (
                <div
                  key={day}
                  style={{
                    padding: "1rem",
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e0e0e0",
                    fontWeight: 600,
                    color: "#666",
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                border: "1px solid #e0e0e0",
                borderTop: "none",
              }}
            >
              {calendarDays.map((day, index) => {
                const isToday = day.isSame(dayjs(), "day");
                const pastDay = day.isBefore(dayjs(), "day");
                const isCurrentMonth = day.isSame(currentDate, "month");
                const dayEvent = getEventsForDate(day);

                return (
                  <div
                    key={index}
                    onClick={() => (pastDay ? undefined : handleDateClick(day, dayEvent))}
                    style={{
                      minHeight: "120px",
                      padding: "0.5rem",
                      cursor: "pointer",
                      backgroundColor: isToday ? "#fff3e0" : isCurrentMonth ? "white" : "#f5f5f5",
                      border: "1px solid #e0e0e0",
                      borderTop: "none",
                      borderLeft: index % 7 === 0 ? "1px solid #e0e0e0" : "none",
                      transition: "background-color 0.2s ease",
                      position: "relative",
                      opacity: pastDay ? 0.5 : 1,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = isToday
                        ? "#fff3e0"
                        : isCurrentMonth
                          ? "white"
                          : "#f5f5f5";
                    }}
                  >
                    {/* Date Number */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: isToday ? 700 : isCurrentMonth ? 600 : 400,
                          color: isToday ? "#d32f2f" : isCurrentMonth ? "#333" : "#999",
                          fontSize: isToday ? "1.1rem" : "1rem",
                        }}
                      >
                        {day.format("D")}
                      </span>

                      {isToday && (
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            backgroundColor: "#d32f2f",
                          }}
                        />
                      )}
                    </div>

                    {/* Events */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                      <Tag
                        key={dayEvent?.id}
                        value={
                          dayEvent.active
                            ? `${dayEvent?.start || ""} - ${dayEvent?.end || ""}`
                            : "Closed"
                        }
                        style={{
                          backgroundColor: dayEvent?.color || "#1976d2",
                          color: "white",
                          fontSize: "0.75rem",
                          padding: "0.125rem 0.5rem",
                          borderRadius: "12px",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      <Dialog
        header={`Edit Schedule - ${selectedException?.date}`}
        visible={!!selectedException}
        style={{ width: "50vw" }}
        onHide={() => {
          setSelectedException(null);
        }}
      >
        <div style={{ padding: "1rem 0" }}>
          {/* Schedule Type Note */}
          <div
            style={{
              backgroundColor: selectedException?.type === "exception" ? "#fff3cd" : "#d1ecf1",
              border: `1px solid ${selectedException?.type === "exception" ? "#ffeaa7" : "#bee5eb"}`,
              borderRadius: "4px",
              padding: "0.75rem",
              marginBottom: "1.5rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <i
                className={`pi ${selectedException?.type === "exception" ? "pi-exclamation-triangle" : "pi-calendar"}`}
                style={{ color: selectedException?.type === "exception" ? "#856404" : "#0c5460" }}
              ></i>
              <span
                style={{
                  fontWeight: 600,
                  color: selectedException?.type === "exception" ? "#856404" : "#0c5460",
                }}
              >
                {selectedException?.type === "exception"
                  ? "Custom Day - Custom schedule for this specific date"
                  : "Default Schedule - Following regular weekly schedule"}
              </span>
            </div>
          </div>

          {/* Active Toggle */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              htmlFor="calendar-active"
              style={{
                fontWeight: 600,
                display: "block",
                marginBottom: "0.5rem",
                color: "#333",
              }}
            >
              Status
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <InputSwitch
                id="calendar-active"
                checked={checked}
                onChange={e => setChecked(e.value)}
              />
              <span style={{ color: checked ? "#4caf50" : "#f44336", fontWeight: 500 }}>
                {checked ? "Open" : "Closed"}
              </span>
            </div>
          </div>

          {/* Time Inputs - Only show when active */}
          {checked && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <label
                  htmlFor="calendar-start"
                  style={{
                    fontWeight: 600,
                    display: "block",
                    marginBottom: "0.5rem",
                    color: "#333",
                  }}
                >
                  Start Time
                </label>
                <Calendar
                  id="calendar-start"
                  value={startTime}
                  onChange={e => setStartTime(e.value || undefined)}
                  timeOnly
                  showTime
                  hourFormat="24"
                  stepMinute={15}
                  style={{ width: "100%" }}
                />
              </div>

              <div>
                <label
                  htmlFor="calendar-end"
                  style={{
                    fontWeight: 600,
                    display: "block",
                    marginBottom: "0.5rem",
                    color: "#333",
                  }}
                >
                  End Time
                </label>
                <Calendar
                  id="calendar-end"
                  value={endTime}
                  onChange={e => setEndTime(e.value || undefined)}
                  timeOnly
                  showTime
                  hourFormat="24"
                  stepMinute={15}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
              borderTop: "1px solid #dee2e6",
              paddingTop: "1rem",
              marginTop: "1rem",
            }}
          >
            {selectedException?.type === "exception" && (
              <Button
                type="button"
                label="Delete Custom Schedule"
                icon="pi pi-trash"
                className="p-button-outlined"
                size="small"
                onClick={() => removeExceptionConfirm()}
                severity="danger"
              />
            )}
            <Button
              type="button"
              label="Save Changes"
              icon="pi pi-save"
              className="p-button-success"
              onClick={() => updateAddException()}
              size="small"
              style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Edit Default Schedule"
        visible={editSchedule}
        style={{ width: "70vw" }}
        onHide={() => setEditSchedule(false)}
      >
        <div style={{ padding: "1rem 0" }}>
          {/* Information Banner */}
          <div
            style={{
              backgroundColor: "#d1ecf1",
              border: "1px solid #bee5eb",
              borderRadius: "4px",
              padding: "0.75rem",
              marginBottom: "1.5rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <i className="pi pi-info-circle" style={{ color: "#0c5460" }}></i>
              <span style={{ fontWeight: 600, color: "#0c5460" }}>
                Set your default weekly schedule. Individual dates can be overridden with
                exceptions.
              </span>
            </div>
          </div>

          {/* Weekly Schedule Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem" }}>
            {scheduleOrder.map((day, index) => {
              const { active, start, end } = schedule[day];
              return (
                <div key={day} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "100px 80px 1fr 1fr",
                      gap: "0.75rem",
                      alignItems: "start",
                    }}
                  >
                    {/* Day Name */}
                    <h4 style={{ margin: 0, fontWeight: 600, color: "#333", fontSize: "1rem" }}>
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </h4>

                    {/* Active Toggle */}
                    <div>
                      <label
                        style={{
                          fontWeight: 600,
                          display: "block",
                          marginBottom: "0.25rem",
                          color: "#333",
                          fontSize: "0.75rem",
                        }}
                      >
                        Status
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          marginTop: 8,
                        }}
                      >
                        <InputSwitch
                          checked={active}
                          onChange={e => {
                            setSchedule(prev => ({
                              ...prev,
                              [day]: {
                                ...prev[day],
                                active: e.value,
                              },
                            }));
                          }}
                          style={{ transform: "scale(0.8)" }}
                        />
                      </div>
                    </div>

                    {/* Start Time */}
                    <div>
                      <label
                        style={{
                          fontWeight: 600,
                          display: "block",
                          marginBottom: "0.25rem",
                          color: active ? "#333" : "#999",
                          fontSize: "0.75rem",
                        }}
                      >
                        Start Time
                      </label>
                      <Calendar
                        value={active && start ? new Date(`2024-01-01 ${start}`) : null}
                        onChange={e => {
                          if (e.value) {
                            const timeString = e.value.toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            });
                            setSchedule(prev => ({
                              ...prev,
                              [day]: {
                                ...prev[day],
                                start: timeString,
                              },
                            }));
                          }
                        }}
                        timeOnly
                        showTime
                        hourFormat="24"
                        stepMinute={15}
                        disabled={!active}
                        style={{ width: "100%", fontSize: "0.8rem" }}
                        placeholder="Start time"
                      />
                    </div>

                    {/* End Time */}
                    <div>
                      <label
                        style={{
                          fontWeight: 600,
                          display: "block",
                          marginBottom: "0.25rem",
                          color: active ? "#333" : "#999",
                          fontSize: "0.75rem",
                        }}
                      >
                        End Time
                      </label>
                      <Calendar
                        value={active && end ? new Date(`2024-01-01 ${end}`) : null}
                        onChange={e => {
                          if (e.value) {
                            const timeString = e.value.toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            });
                            setSchedule(prev => ({
                              ...prev,
                              [day]: {
                                ...prev[day],
                                end: timeString,
                              },
                            }));
                          }
                        }}
                        timeOnly
                        showTime
                        hourFormat="24"
                        stepMinute={15}
                        disabled={!active}
                        style={{ width: "100%", fontSize: "0.8rem" }}
                        placeholder="End time"
                      />
                    </div>
                  </div>

                  {/* Divider - Show after every day except the last */}
                  {index <
                    ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
                      .length -
                    1 && (
                      <hr
                        style={{
                          border: "none",
                          borderTop: "1px solid #dee2e6",
                          margin: "0.5rem 0",
                        }}
                      />
                    )}
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
              borderTop: "1px solid #dee2e6",
              paddingTop: "1rem",
              marginTop: "1rem",
            }}
          >
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined"
              size="small"
              onClick={() => setEditSchedule(false)}
              style={{ color: "#6c757d" }}
            />
            <Button
              type="button"
              label="Save Schedule"
              icon="pi pi-save"
              className="p-button-success"
              size="small"
              onClick={() => {
                // Here you would save the schedule to your backend/state
                console.log("Saving schedule:", schedule);
                setEditSchedule(false);
                updateSiteSchedule();
              }}
              style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
            />
          </div>
        </div>
      </Dialog>
      <ConfirmDialog />
    </Fragment>
  );
}

export default SiteCalenderView;
